import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiPlusCircle, FiUploadCloud, FiCalendar } from "react-icons/fi";
import api from "../api";
import EventStatsCard from "../components/EventStatsCard";
import SocialStats from "../components/SocialStats";
import ActivityCard from "../components/ActivityCard";
import { aggregateStats } from "../utils/Stats";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

const QUICK_ACTIONS = [
  {
    Icon: FiPlusCircle,
    labelKey: "admin_home.quick_action_new_event",
    href: "/admin/events",
    color: "#6366f1",
    bg: "#eef2ff",
  },
  {
    Icon: FiCalendar,
    labelKey: "admin_home.quick_action_my_events",
    href: "/admin/events",
    color: "#0ea5e9",
    bg: "#f0f9ff",
  },
  {
    Icon: FiUploadCloud,
    labelKey: "admin_home.quick_action_upload",
    href: "/admin/events",
    color: "#10b981",
    bg: "#f0fdf4",
  },
];

const HomeAdmin = ({ user }) => {
  const { events, setEvents } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [aggregates, setAggregates] = useState({
    totalStats: {},
    photoStats: {},
    socialStats: {},
  });

  const currentDate = new Date();
  const dateMinus24h = new Date(currentDate.getTime() - 24 * 60 * 60 * 1000);

  function toMysqlDatetime(date) {
    const pad = (n) => (n < 10 ? "0" + n : n);
    return (
      date.getFullYear() + "-" +
      pad(date.getMonth() + 1) + "-" +
      pad(date.getDate()) + " " +
      pad(date.getHours()) + ":" +
      pad(date.getMinutes()) + ":" +
      pad(date.getSeconds())
    );
  }

  const startDateTime = toMysqlDatetime(dateMinus24h);
  const endDateTime = toMysqlDatetime(currentDate);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/events?user_id=${user.user_id}`, {
          withCredentials: true,
        });
        setEvents(res.data || []);
        const { totalStats, photoStats, socialStats } = aggregateStats(res.data || []);
        setAggregates({ totalStats, photoStats, socialStats });
      } catch (err) {
        console.error("Error fetching events:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.user_id) fetchEvents();
  }, [user]);

  if (loading) {
    return <div className="text-center py-5 text-muted">Loading dashboard...</div>;
  }

  return (
    <Container className="mt-3">

      {/* ── Quick actions ── */}
      <Row className="mb-3">
        <Col>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 10 }}>
            {t("admin_home.quick_actions_title")}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {QUICK_ACTIONS.map(({ Icon, labelKey, href, color, bg }) => (
              <Link
                key={labelKey}
                to={href}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 18px",
                  borderRadius: 12,
                  background: bg,
                  color,
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: "none",
                  border: "none",
                  transition: "opacity 0.15s, transform 0.1s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <Icon size={16} aria-hidden="true" />
                {t(labelKey)}
              </Link>
            ))}
          </div>
        </Col>
      </Row>

      {/* ── Stats ── */}
      <Row>
        <Col md={6} className="mb-3">
          <EventStatsCard stats={aggregates.totalStats} />
        </Col>
        <Col md={12} className="mb-3">
          <SocialStats stats={aggregates.socialStats} />
        </Col>
        <Col md={12} className="mb-3">
          <ActivityCard userId={user.user_id} startDateTime={startDateTime} endDateTime={endDateTime} />
        </Col>
      </Row>
    </Container>
  );
};

export default HomeAdmin;
